import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Rocket, Search, SlidersHorizontal, Loader2 } from "lucide-react";
import { useLaunchpadTokens, useLaunchpadConfig } from "../../hooks/useLaunchpad";
import { TokenCard } from "./TokenCard";
import { type MemeToken, TokenStatus } from "../../config/tokens";
import { formatTokenAmount, cn } from "../../lib/utils";

type Filter = "all" | "live" | "graduated" | "rising";
type SortBy = "newest" | "reserve" | "progress";

export function LaunchpadPage() {
  const [filter, setFilter] = useState<Filter>("all");
  const [sortBy, setSortBy] = useState<SortBy>("newest");
  const [search, setSearch] = useState("");

  const { tokens, liveTokens, isLoading } = useLaunchpadTokens(0n, 100n);
  const { tokenCount } = useLaunchpadConfig();

  const displayTokens = useMemo(() => {
    let list: MemeToken[] = [];

    switch (filter) {
      case "all":
        list = [...tokens];
        break;
      case "live":
        list = tokens.filter((t) => t.status === TokenStatus.Live);
        break;
      case "graduated":
        list = tokens.filter((t) => t.status === TokenStatus.Graduated);
        break;
      case "rising":
        list = tokens
          .filter((t) => t.status === TokenStatus.Live)
          .sort((a, b) => (b.tradingVolume > a.tradingVolume ? 1 : -1))
          .slice(0, 20);
        break;
    }

    if (search) {
      const q = search.toLowerCase();
      list = list.filter(
        (t) =>
          t.name.toLowerCase().includes(q) ||
          t.symbol.toLowerCase().includes(q) ||
          t.tokenAddress.toLowerCase().includes(q)
      );
    }

    switch (sortBy) {
      case "newest":
        list.sort((a, b) => (b.createdAt > a.createdAt ? 1 : -1));
        break;
      case "reserve":
        list.sort((a, b) => (b.reserveBalance > a.reserveBalance ? 1 : -1));
        break;
      case "progress":
        list.sort((a, b) => (b.tokensSold > a.tokensSold ? 1 : -1));
        break;
    }

    return list;
  }, [tokens, filter, sortBy, search]);

  const filters: { key: Filter; label: string }[] = [
    { key: "all", label: "All" },
    { key: "live", label: "Live" },
    { key: "graduated", label: "Graduated" },
    { key: "rising", label: "Rising" },
  ];

  const sorts: { key: SortBy; label: string }[] = [
    { key: "newest", label: "Newest" },
    { key: "reserve", label: "Reserve" },
    { key: "progress", label: "Progress" },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-8 pb-20">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-3xl font-bold gradient-text mb-1">
            Launchpad
          </h1>
          <p className="text-text-secondary text-sm">
            {Number(tokenCount)} tokens launched on JasmyChain
          </p>
        </div>

        <Link
          to="/launchpad/launch"
          className="btn-primary flex items-center gap-2 text-sm"
        >
          <Rocket className="w-4 h-4" />
          Launch Token
        </Link>
      </div>

      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-tertiary" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, symbol, or address"
            className="input-field pl-10 text-sm"
          />
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex gap-1 p-1 bg-surface/50 rounded-button">
            {filters.map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={cn(
                  "px-3 py-1.5 rounded-lg text-xs font-medium transition-all",
                  filter === f.key
                    ? "bg-primary/15 text-primary"
                    : "text-text-secondary hover:text-text-primary"
                )}
              >
                {f.label}
              </button>
            ))}
          </div>

          <div className="flex gap-1 p-1 bg-surface/50 rounded-button">
            {sorts.map((s) => (
              <button
                key={s.key}
                onClick={() => setSortBy(s.key)}
                className={cn(
                  "px-3 py-1.5 rounded-lg text-xs font-medium transition-all",
                  sortBy === s.key
                    ? "bg-secondary/15 text-secondary"
                    : "text-text-secondary hover:text-text-primary"
                )}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-primary animate-spin" />
        </div>
      ) : displayTokens.length === 0 ? (
        <div className="text-center py-20">
          <Rocket className="w-12 h-12 text-text-tertiary mx-auto mb-4" />
          <h3 className="font-display text-lg text-text-secondary mb-2">
            No tokens found
          </h3>
          <p className="text-sm text-text-tertiary mb-6">
            {search
              ? "Try a different search term"
              : "Be the first to launch a token on JasmyChain"}
          </p>
          <Link
            to="/launchpad/launch"
            className="btn-primary inline-flex items-center gap-2"
          >
            <Rocket className="w-4 h-4" />
            Launch Token
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {displayTokens.map((token) => (
            <TokenCard key={token.id.toString()} token={token} />
          ))}
        </div>
      )}
    </div>
  );
}
